import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  AccountTree as DependencyIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { Expert, ExpertExecutionResult } from '../../types';
import { expertApi } from '../../services/api';
import {
  formatModelPricing,
  getModelDisplayName,
} from '../../utils/modelPricing';
import { UsageBreakdownPanel } from '../common';

interface ExpertDependency {
  name: string;
  expert_id: number;
  expert_name?: string;
}

interface PastExecution {
  id: string;
  status: string;
  created_at: string;
  parsed_output: Record<string, any>;
}

interface TestExpertDialogProps {
  open: boolean;
  onClose: () => void;
  expert: Expert;
}

export const TestExpertDialog: React.FC<TestExpertDialogProps> = ({
  open,
  onClose,
  expert,
}) => {
  const [isExecuting, setIsExecuting] = useState(false);
  const [result, setResult] = useState<ExpertExecutionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  // Expert dependency overrides state
  const [overrideEnabled, setOverrideEnabled] = useState<Record<number, boolean>>({});
  const [overrideValues, setOverrideValues] = useState<Record<number, string>>({});
  const [overrideErrors, setOverrideErrors] = useState<Record<number, string>>({});
  const [overrideSource, setOverrideSource] = useState<Record<number, 'manual' | 'history'>>({});
  const [selectedExecution, setSelectedExecution] = useState<Record<number, string>>({});
  
  // Past executions for each dependency
  const [pastExecutions, setPastExecutions] = useState<Record<number, PastExecution[]>>({});
  const [loadingExecutions, setLoadingExecutions] = useState<Record<number, boolean>>({});

  // Extract expert dependencies from input_data
  const expertDependencies = useMemo<ExpertDependency[]>(() => {
    if (!expert.input_data || typeof expert.input_data !== 'object') {
      return [];
    }
    
    const inputs = expert.input_data.inputs;
    if (!Array.isArray(inputs)) {
      return [];
    }
    
    return inputs
      .filter((input: any) => input.type === 'expert' && input.expert_id)
      .map((input: any) => ({
        name: input.name || `expert_${input.expert_id}`,
        expert_id: input.expert_id,
        expert_name: input.expert_name,
      }));
  }, [expert.input_data]);

  // Fetch expert names and past executions for dependencies
  useEffect(() => {
    const fetchDependencyData = async () => {
      for (const dep of expertDependencies) {
        // Fetch expert name if missing
        if (!dep.expert_name && dep.expert_id) {
          try {
            const expertData = await expertApi.get(dep.expert_id);
            dep.expert_name = expertData.name;
          } catch (e) {
            // Keep using expert_id as fallback
          }
        }
        
        // Fetch past executions for this dependency
        if (dep.expert_id && !pastExecutions[dep.expert_id]) {
          setLoadingExecutions(prev => ({ ...prev, [dep.expert_id]: true }));
          try {
            const executions = await expertApi.getExecutionHistory(dep.expert_id, 10);
            const formattedExecutions: PastExecution[] = executions
              .filter((exec: any) => exec.status === 'SUCCESS' || exec.status === 'success')
              .map((exec: any) => ({
                id: exec.id,
                status: exec.status,
                created_at: exec.created_at,
                parsed_output: exec.parsed_output || {},
              }));
            setPastExecutions(prev => ({ ...prev, [dep.expert_id]: formattedExecutions }));
          } catch (e) {
            console.error(`Failed to fetch executions for expert ${dep.expert_id}:`, e);
            setPastExecutions(prev => ({ ...prev, [dep.expert_id]: [] }));
          } finally {
            setLoadingExecutions(prev => ({ ...prev, [dep.expert_id]: false }));
          }
        }
      }
    };
    
    if (expertDependencies.length > 0 && open) {
      fetchDependencyData();
    }
  }, [expertDependencies, open]);

  const handleTest = async () => {
    setIsExecuting(true);
    setError(null);
    setResult(null);

    // Validate and build expert overrides
    const newOverrideErrors: Record<number, string> = {};
    const expertOverrides: Record<string, any> = {};
    
    for (const dep of expertDependencies) {
      if (overrideEnabled[dep.expert_id]) {
        const valueStr = overrideValues[dep.expert_id] || '';
        if (!valueStr.trim()) {
          newOverrideErrors[dep.expert_id] = 'Override value cannot be empty';
          continue;
        }
        
        try {
          // Parse JSON value
          const parsedValue = JSON.parse(valueStr);
          expertOverrides[String(dep.expert_id)] = parsedValue;
        } catch (e) {
          newOverrideErrors[dep.expert_id] = 'Invalid JSON format';
        }
      }
    }
    
    setOverrideErrors(newOverrideErrors);
    
    if (Object.keys(newOverrideErrors).length > 0) {
      setIsExecuting(false);
      setError('Please fix the override value errors before executing');
      return;
    }

    try {
      // Use the expert's configured input_data
      const parsedInput = expert.input_data || {};

      // Call API with optional expert overrides
      const hasOverrides = Object.keys(expertOverrides).length > 0;
      const response = await expertApi.testExecute(
        expert.id, 
        parsedInput,
        hasOverrides ? expertOverrides : undefined
      );

      if (response.success && response.result) {
        setResult(response.result);
      } else {
        setError(response.error || 'Execution failed');
      }
    } catch (err: any) {
      console.error('Expert execution error:', err);
      
      // Better error handling
      let errorMessage = 'Failed to execute expert';
      
      if (err.code === 'ECONNABORTED') {
        errorMessage = 'Execution timed out. The expert took too long to respond. This may be due to slow LLM API response or network issues.';
      } else if (err.response) {
        // Server responded with an error
        errorMessage = err.response.data?.error || err.response.data?.detail || `Server error: ${err.response.status}`;
      } else if (err.request) {
        // Request made but no response
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleClose = () => {
    setResult(null);
    setError(null);
    onClose();
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' => {
    switch (status.toLowerCase()) {
      case 'success':
      case 'cached':
        return 'success';
      case 'error':
      case 'failed':
        return 'error';
      case 'running':
        return 'warning';
      default:
        return 'info';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success':
      case 'cached':
        return <SuccessIcon />;
      case 'error':
      case 'failed':
        return <ErrorIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const dialogContent = (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{ zIndex: 9999 }}
      slotProps={{
        backdrop: {
          sx: { zIndex: 9998 }
        }
      }}
      PaperProps={{
        sx: { minHeight: '70vh', maxHeight: '90vh', zIndex: 9999 },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">Test Expert: {expert.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              Type: {expert.expert_type} | Model: {expert.model_name || 'N/A'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Display Configured Input Data */}
          <Paper elevation={1} sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Configured Input Data
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: isDark ? 'grey.900' : 'grey.50',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                maxHeight: 200,
                overflow: 'auto',
              }}
            >
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(expert.input_data || {}, null, 2)}
              </pre>
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This expert will be tested with its configured input data.
            </Typography>
          </Paper>

          {/* Expert Dependencies Override Section */}
          {expertDependencies.length > 0 && (
            <Paper elevation={1} sx={{ p: 2, bgcolor: 'background.default' }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <DependencyIcon color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Expert Dependencies ({expertDependencies.length})
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Override dependent expert outputs with custom values for testing. 
                When enabled, the custom JSON value will be used instead of executing the dependency.
              </Typography>
              
              {expertDependencies.map((dep) => (
                <Paper 
                  key={dep.expert_id} 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    mb: 2,
                    borderColor: overrideEnabled[dep.expert_id] ? 'primary.main' : 'divider',
                    bgcolor: overrideEnabled[dep.expert_id] ? 'action.hover' : 'transparent',
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box>
                      <Typography variant="subtitle2">
                        {dep.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Expert ID: {dep.expert_id}
                        {dep.expert_name && ` • ${dep.expert_name}`}
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={overrideEnabled[dep.expert_id] || false}
                          onChange={(e) => {
                            setOverrideEnabled(prev => ({
                              ...prev,
                              [dep.expert_id]: e.target.checked
                            }));
                            // Clear error when toggling off
                            if (!e.target.checked) {
                              setOverrideErrors(prev => {
                                const updated = { ...prev };
                                delete updated[dep.expert_id];
                                return updated;
                              });
                            }
                          }}
                          size="small"
                        />
                      }
                      label="Override"
                      labelPlacement="start"
                    />
                  </Box>
                  
                  {overrideEnabled[dep.expert_id] && (
                    <Box sx={{ mt: 2 }}>
                      {/* Source selector: Manual or Past Execution */}
                      <Box display="flex" gap={1} mb={2}>
                        <Chip
                          label="Manual Input"
                          variant={overrideSource[dep.expert_id] !== 'history' ? 'filled' : 'outlined'}
                          color={overrideSource[dep.expert_id] !== 'history' ? 'primary' : 'default'}
                          onClick={() => {
                            setOverrideSource(prev => ({ ...prev, [dep.expert_id]: 'manual' }));
                            setSelectedExecution(prev => {
                              const updated = { ...prev };
                              delete updated[dep.expert_id];
                              return updated;
                            });
                          }}
                          size="small"
                        />
                        <Chip
                          icon={<HistoryIcon />}
                          label={`Past Executions ${pastExecutions[dep.expert_id]?.length ? `(${pastExecutions[dep.expert_id].length})` : ''}`}
                          variant={overrideSource[dep.expert_id] === 'history' ? 'filled' : 'outlined'}
                          color={overrideSource[dep.expert_id] === 'history' ? 'primary' : 'default'}
                          onClick={() => setOverrideSource(prev => ({ ...prev, [dep.expert_id]: 'history' }))}
                          size="small"
                          disabled={!pastExecutions[dep.expert_id]?.length && !loadingExecutions[dep.expert_id]}
                        />
                        {loadingExecutions[dep.expert_id] && (
                          <CircularProgress size={20} sx={{ ml: 1 }} />
                        )}
                      </Box>

                      {/* Past Execution Selector */}
                      {overrideSource[dep.expert_id] === 'history' && (
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                          <InputLabel>Select Past Execution</InputLabel>
                          <Select
                            value={selectedExecution[dep.expert_id] || ''}
                            label="Select Past Execution"
                            onChange={(e) => {
                              const execId = e.target.value;
                              setSelectedExecution(prev => ({ ...prev, [dep.expert_id]: execId }));
                              
                              // Auto-populate the override value with the selected execution's output
                              const execution = pastExecutions[dep.expert_id]?.find(ex => ex.id === execId);
                              if (execution) {
                                setOverrideValues(prev => ({
                                  ...prev,
                                  [dep.expert_id]: JSON.stringify(execution.parsed_output, null, 2)
                                }));
                                // Clear any errors
                                setOverrideErrors(prev => {
                                  const updated = { ...prev };
                                  delete updated[dep.expert_id];
                                  return updated;
                                });
                              }
                            }}
                            MenuProps={{
                              sx: { zIndex: 9999 },
                            }}
                          >
                            {pastExecutions[dep.expert_id]?.map((exec) => (
                              <MenuItem key={exec.id} value={exec.id}>
                                <Box>
                                  <Typography variant="body2">
                                    {new Date(exec.created_at).toLocaleString()}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {exec.status} • {Object.keys(exec.parsed_output || {}).length} output fields
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      {/* Override Value Editor */}
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={8}
                        label="Override Value (JSON)"
                        placeholder='{"key": "value", "analysis": "custom result..."}'
                        value={overrideValues[dep.expert_id] || ''}
                        onChange={(e) => {
                          setOverrideValues(prev => ({
                            ...prev,
                            [dep.expert_id]: e.target.value
                          }));
                          // Clear error on change
                          if (overrideErrors[dep.expert_id]) {
                            setOverrideErrors(prev => {
                              const updated = { ...prev };
                              delete updated[dep.expert_id];
                              return updated;
                            });
                          }
                        }}
                        error={!!overrideErrors[dep.expert_id]}
                        helperText={overrideErrors[dep.expert_id] || 'Enter valid JSON that represents the expert output'}
                        size="small"
                        sx={{
                          '& .MuiInputBase-input': {
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                          }
                        }}
                      />
                    </Box>
                  )}
                </Paper>
              ))}
            </Paper>
          )}

          {/* Error Display */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Results Display */}
          {result && (
            <Paper elevation={2} sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6">Execution Results</Typography>
                <Chip
                  icon={getStatusIcon(result.status)}
                  label={result.status.toUpperCase()}
                  color={getStatusColor(result.status)}
                  size="small"
                />
              </Box>

              {/* Summary Stats */}
              <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                <Chip
                  label={`Cost: $${result.total_cost.toFixed(4)}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Tokens: ${result.total_token.toFixed(0)}`}
                  size="small"
                  variant="outlined"
                />
                <Tooltip title={formatModelPricing(result)} arrow>
                  <Chip
                    label={`Model: ${getModelDisplayName(result, 'No model')}`}
                    size="small"
                    variant="outlined"
                  />
                </Tooltip>
                {result.cache_key && (
                  <Chip
                    label="Cached Result"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Box>

              <Box mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Input / Output Price
                </Typography>
                <Typography variant="body2">
                  {formatModelPricing(result)}
                </Typography>
              </Box>
              <UsageBreakdownPanel
                usage={result.usage_breakdown}
                title="Token cost breakdown"
                compact
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              {/* Parsed Output */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Parsed Output</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ position: 'relative' }}>
                    <Tooltip title="Copy to clipboard">
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', right: 0, top: 0 }}
                        onClick={() =>
                          handleCopy(JSON.stringify(result.parsed_output, null, 2))
                        }
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: isDark ? 'grey.900' : 'grey.50',
                        maxHeight: 300,
                        overflow: 'auto',
                      }}
                    >
                      <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                        {JSON.stringify(result.parsed_output, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Raw Output */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Raw Output</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ position: 'relative' }}>
                    <Tooltip title="Copy to clipboard">
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', right: 0, top: 0 }}
                        onClick={() => handleCopy(result.raw_output)}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: isDark ? 'grey.900' : 'grey.50',
                        maxHeight: 300,
                        overflow: 'auto',
                      }}
                    >
                      <Typography
                        component="pre"
                        sx={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}
                      >
                        {result.raw_output}
                      </Typography>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Prompt */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Prompt Sent to LLM</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ position: 'relative' }}>
                    <Tooltip title="Copy to clipboard">
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', right: 0, top: 0 }}
                        onClick={() => handleCopy(result.prompt)}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: isDark ? 'grey.900' : 'grey.50',
                        maxHeight: 300,
                        overflow: 'auto',
                      }}
                    >
                      <Typography
                        component="pre"
                        sx={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}
                      >
                        {result.prompt}
                      </Typography>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Sent Files */}
              {result.metadata?.files && Array.isArray(result.metadata.files) && result.metadata.files.length > 0 && (
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Sent Files ({result.metadata.files.length})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {result.metadata.files.map((file: any, index: number) => (
                        <Paper key={index} variant="outlined" sx={{ p: 1, maxWidth: 300 }}>
                          <Typography variant="caption" display="block" gutterBottom title={file.filename}>
                            {file.filename || `File ${index + 1}`}
                          </Typography>
                          {file.mime_type && file.mime_type.startsWith('image/') && file.base64 ? (
                            <img
                              src={`data:${file.mime_type};base64,${file.base64}`}
                              alt={file.filename}
                              style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', display: 'block' }}
                            />
                          ) : (
                            <Box sx={{ p: 2, bgcolor: isDark ? 'grey.800' : 'grey.100', textAlign: 'center', minWidth: 150 }}>
                              <Typography variant="body2">
                                {file.mime_type || 'Unknown type'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {Math.round((file.base64?.length || 0) / 1024)} KB
                              </Typography>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Tool Calls - Only show for AGENT type experts */}
              {expert.expert_type === 'AGENT' && result.metadata?.tool_calls && Array.isArray(result.metadata.tool_calls) && result.metadata.tool_calls.length > 0 && (
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2">
                        Tool Calls ({result.metadata.tool_calls.length})
                      </Typography>
                      <Chip 
                        size="small" 
                        label="AGENT" 
                        color="primary" 
                        variant="outlined"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {result.metadata.tool_calls.map((toolCall: any, index: number) => (
                        <Paper 
                          key={index} 
                          variant="outlined" 
                          sx={{ p: 2, bgcolor: isDark ? 'grey.900' : 'grey.50' }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                size="small" 
                                label={`Step ${toolCall.step_number}`} 
                                color="default"
                              />
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                {toolCall.tool_name}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {toolCall.duration_ms > 0 && (
                                <Chip size="small" label={`${toolCall.duration_ms}ms`} variant="outlined" />
                              )}
                              {toolCall.tokens_used > 0 && (
                                <Chip size="small" label={`${toolCall.tokens_used} tokens`} variant="outlined" />
                              )}
                            </Box>
                          </Box>
                          
                          {/* Tool Input */}
                          {toolCall.tool_input && (
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                Input:
                              </Typography>
                              <Paper 
                                variant="outlined" 
                                sx={{ p: 1, mt: 0.5, bgcolor: 'background.paper', maxHeight: 150, overflow: 'auto' }}
                              >
                                <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                                  {JSON.stringify(toolCall.tool_input, null, 2)}
                                </pre>
                              </Paper>
                            </Box>
                          )}
                          
                          {/* Tool Output */}
                          {toolCall.tool_output && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                Output:
                              </Typography>
                              {/* Render image preview if tool output contains file attachment */}
                              {toolCall.tool_output?.data?._is_file_attachment && 
                               toolCall.tool_output?.data?.base64 && 
                               toolCall.tool_output?.data?.mime_type?.startsWith('image/') && (
                                <Paper
                                  variant="outlined"
                                  sx={{
                                    p: 1,
                                    mt: 0.5,
                                    mb: 1,
                                    bgcolor: '#f0f7ff',
                                    textAlign: 'center',
                                  }}
                                >
                                  <Typography variant="caption" color="primary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                    📊 Chart Image
                                  </Typography>
                                  <img 
                                    src={`data:${toolCall.tool_output.data.mime_type};base64,${toolCall.tool_output.data.base64}`}
                                    alt={toolCall.tool_output.data.filename || 'Chart Image'}
                                    style={{ 
                                      maxWidth: '100%', 
                                      maxHeight: '200px', 
                                      borderRadius: '4px',
                                      border: '1px solid #e0e0e0',
                                    }}
                                  />
                                  {toolCall.tool_output.data.symbol && toolCall.tool_output.data.timeframe && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      {toolCall.tool_output.data.symbol} / {toolCall.tool_output.data.timeframe}
                                    </Typography>
                                  )}
                                </Paper>
                              )}
                              <Paper 
                                variant="outlined" 
                                sx={{ p: 1, mt: 0.5, bgcolor: 'background.paper', maxHeight: 150, overflow: 'auto' }}
                              >
                                <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                                  {typeof toolCall.tool_output === 'string' 
                                    ? toolCall.tool_output 
                                    : JSON.stringify(
                                        toolCall.tool_output?.data?.base64 
                                          ? { 
                                              ...toolCall.tool_output, 
                                              data: { 
                                                ...toolCall.tool_output.data, 
                                                base64: `[${toolCall.tool_output.data.base64.length} chars]` 
                                              } 
                                            }
                                          : toolCall.tool_output, 
                                        null, 
                                        2
                                      )}
                                </pre>
                              </Paper>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Agent Info - Only show for AGENT type experts */}
              {expert.expert_type === 'AGENT' && result.metadata?.delegated_execution && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Agent Execution Info</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">Agent:</Typography>
                        <Typography variant="body2">{result.metadata.agent_name}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">Execution ID:</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {result.metadata.agent_execution_id}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">Status:</Typography>
                        <Chip 
                          size="small" 
                          label={result.metadata.agent_status} 
                          color={result.metadata.agent_status === 'COMPLETED' ? 'success' : 'error'}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">Total Steps:</Typography>
                        <Typography variant="body2">{result.metadata.total_steps || 'N/A'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">Tool Calls:</Typography>
                        <Typography variant="body2">{result.metadata.tool_calls?.length || 0}</Typography>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Logs */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Execution Logs</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ position: 'relative' }}>
                    <Tooltip title="Copy to clipboard">
                      <IconButton
                        size="small"
                        sx={{ position: 'absolute', right: 0, top: 0 }}
                        onClick={() => handleCopy(result.logs)}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        bgcolor: isDark ? 'grey.900' : 'grey.50',
                        maxHeight: 300,
                        overflow: 'auto',
                      }}
                    >
                      <Typography
                        component="pre"
                        sx={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}
                      >
                        {result.logs}
                      </Typography>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Metadata */}
              {result.metadata && Object.keys(result.metadata).length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">Metadata</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ position: 'relative' }}>
                      <Tooltip title="Copy to clipboard">
                        <IconButton
                          size="small"
                          sx={{ position: 'absolute', right: 0, top: 0 }}
                          onClick={() => {
                            // Create a copy with truncated files for clipboard to avoid hanging
                            const metadataCopy = { ...result.metadata };
                            if (metadataCopy.files) {
                              metadataCopy.files = `[${metadataCopy.files.length} files hidden]`;
                            }
                            handleCopy(JSON.stringify(metadataCopy, null, 2));
                          }}
                        >
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          bgcolor: isDark ? 'grey.900' : 'grey.50',
                          maxHeight: 300,
                          overflow: 'auto',
                        }}
                      >
                        <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                          {(() => {
                            // Create a display version of metadata with files hidden
                            const displayMetadata = { ...result.metadata };
                            if (displayMetadata.files) {
                              displayMetadata.files = `[${displayMetadata.files.length} files displayed above]`;
                            }
                            return JSON.stringify(displayMetadata, null, 2);
                          })()}
                        </pre>
                      </Paper>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Paper>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={isExecuting}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleTest}
          disabled={isExecuting}
          startIcon={isExecuting ? <CircularProgress size={20} /> : <PlayIcon />}
        >
          {isExecuting ? 'Executing...' : 'Test Expert'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Render using Portal to escape parent dialog's stacking context
  return open ? createPortal(dialogContent, document.body) : null;
};
